/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable jsx-a11y/alt-text */
import React, {FC, useEffect, useState, useCallback, useMemo, lazy, Suspense} from 'react'
import {Alert, Nav, Tab} from 'react-bootstrap'
import {useDispatch, useSelector} from 'react-redux'
import DatePicker from 'react-datepicker'
import Select from 'react-select'
import moment from 'moment'

// Styles
import './DashboardPage.scss'
import './EnhancedDashboard.scss'

// Components
import {KTSVG} from '../../../../_metronic/helpers'
import {
  getUserData,
  sendEmailOtp,
} from '../../../modules/auth/components/Frontend_Auth_Components/UserLoginRedux/UserLoginSlice'
import {fetchAsyncUserDetails} from '../../UserPages/accounts/Overview/OverviewSlice/UserDetailsSlice'
import {
  BackendDashboardSalesGraph,
  BackendNewMessageLineGraph,
  getBEDashboardGraphLine,
} from './BackendDashboardSlice/BackendDashboardSlice'
import {GeneralDataOFLoginUser} from '../../../Utils/Auth'
import {
  GetAllAccountId,
  getAccountIdList,
  getAccountManageList,
  getSalesList,
} from '../AllCellCastUser/UserListSlice/UserListSlice'
import {isAdmin, isStaff} from '../../../Utils/_helpers'
import {
  useGetAdminDashboardActivityOverviewCountQuery,
  useGetAdminDashboardSalesContactsCountQuery,
  useGetAdminDashboardMessageSentWithNewClientsBarGraphQuery,
} from '../../../../services/cellcast'

// Split Components
import {
  TabItem,
  LoadingFallback,
  MessageSentCard,
  COGCard,
  SalesContactsCard,
  ChartCard,
  MessageTrendsChart,
} from './components'

// Lazy load heavy components
const NotificationTable = lazy(() => import('./NotificationTable'))
const NewUserTable = lazy(() => import('./NewUserTable'))
const DashboardTablePage = lazy(() => import('./DashboardTablePage'))
const SupportTicketModule = lazy(() => import('./SupportTicketModule'))
const ConfigDashboard = lazy(() => import('./ConfigDashboardCard'))

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const DashboardPageEnhanced: FC = () => {
  const dispatch = useDispatch()

  // Selectors
  const accountId = GeneralDataOFLoginUser()?.accountId
  const Admin = GeneralDataOFLoginUser()
  const UserDetails: any = useSelector(getUserData)
  const accountIdList = useSelector(getAccountIdList)
  const LineGraphData = useSelector(getBEDashboardGraphLine)

  // Constants
  const isUserWhiteLabel = GeneralDataOFLoginUser()?.isWhiteLabelUser === true
  const today = new Date()
  const sevenDaysAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000)
  const email = UserDetails?.data?.user?.email

  // State
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_visible, setVisible] = useState(false)
  const [whereWhen, setWhereWhen] = useState<any>({start: sevenDaysAgo, end: new Date()})
  const UserAccountId = isUserWhiteLabel ? accountId : ''
  const [accountIdData, setAccountIdData] = useState(UserAccountId)
  const [type, setType] = useState<'qm' | 'bm' | 'api'>('qm')
  const [activeTab, setActiveTab] = useState('notifications')

  // RTK Query params - memoized to prevent unnecessary re-fetches
  const dashboardQueryParams = useMemo(
    () => ({
      fromDate: moment(whereWhen.start).format('YYYY-MM-DD'),
      toDate: moment(whereWhen.end).format('YYYY-MM-DD'),
      accountId: accountIdData,
    }),
    [whereWhen.start, whereWhen.end, accountIdData]
  )

  // RTK Query hooks - these automatically cache and deduplicate requests
  // Use isFetching instead of isLoading to show loading state on refetch (when filters change)
  const {data: activityOverviewData, isFetching: isFetchingActivityOverview} =
    useGetAdminDashboardActivityOverviewCountQuery(dashboardQueryParams)

  const {data: salesContactsData, isFetching: isFetchingSalesContacts} =
    useGetAdminDashboardSalesContactsCountQuery(dashboardQueryParams)

  const {data: barGraphData, isFetching: isFetchingBarGraph} =
    useGetAdminDashboardMessageSentWithNewClientsBarGraphQuery(dashboardQueryParams)

  // Extract data from RTK Query responses
  const AdminDashboardCount = activityOverviewData?.data
  const AdminDashboardCountLoading = isFetchingActivityOverview
  const AdminDashboardSalesContacts = salesContactsData?.data
  const AdminDashboardSalesContactsLoading = isFetchingSalesContacts
  const GraphData = barGraphData?.data
  const GraphDataLoading = isFetchingBarGraph

  // Tab configuration based on user role
  const tabConfig: TabItem[] = useMemo(() => {
    const tabs: TabItem[] = [
      {
        id: 'notifications',
        label: 'Notifications',
        icon: '/media/icons/duotune/general/gen007.svg',
      },
      {
        id: 'analytics',
        label: 'Analytics',
        icon: '/media/icons/duotune/graphs/gra001.svg',
      },
      {
        id: 'users',
        label: 'New Users',
        icon: '/media/icons/duotune/communication/com006.svg',
      },
    ]

    // Admin-only tabs
    if (isAdmin(Admin?.role)) {
      tabs.push(
        {
          id: 'trends',
          label: 'Message Trends',
          icon: '/media/icons/duotune/graphs/gra004.svg',
          adminOnly: true,
        },
        {
          id: 'table',
          label: 'Recent Campaign',
          icon: '/media/icons/duotune/general/gen019.svg',
          adminOnly: true,
        }
      )
    }

    // Staff & Admin tabs
    if (isStaff(Admin?.role, true) || isAdmin(Admin?.role)) {
      tabs.push(
        {
          id: 'tickets',
          label: 'Credit Sale',
          icon: '/media/icons/duotune/communication/com003.svg',
          staffOnly: true,
        },
        {
          id: 'config',
          label: 'User Activity',
          icon: '/media/icons/duotune/coding/cod001.svg',
          staffOnly: true,
        }
      )
    }

    return tabs
  }, [Admin?.role])

  // Initial data fetch
  useEffect(() => {
    dispatch(fetchAsyncUserDetails())
    dispatch(getAccountManageList())
    dispatch(getSalesList())
  }, [dispatch])

  // Fetch account IDs list
  useEffect(() => {
    dispatch(GetAllAccountId())
  }, [whereWhen, accountIdData, dispatch])

  // Fetch line graph data when message trends tab is active
  useEffect(() => {
    if (activeTab === 'trends') {
      dispatch(
        BackendNewMessageLineGraph({
          fromDate: moment(whereWhen.start).format('YYYY-MM-DD'),
          toDate: moment(whereWhen.end).format('YYYY-MM-DD'),
          accountId: accountIdData,
          graphFilter: type,
        })
      )
      dispatch(
        BackendDashboardSalesGraph({
          fromDate: moment(whereWhen.start).format('YYYY-MM-DD'),
          toDate: moment(whereWhen.end).format('YYYY-MM-DD'),
          accountId: accountIdData,
        })
      )
    }
  }, [activeTab, type, whereWhen, accountIdData, dispatch])

  // Memoized data transformations
  const dateRangeText = useMemo(
    () =>
      `${moment(whereWhen?.start).format('MMM DD')} - ${moment(whereWhen?.end).format(
        'MMM DD YY'
      )}`,
    [whereWhen]
  )

  const createBarChartOptions = useCallback(
    (AllGraphData: any) => {
      const data = AllGraphData?.graphData?.map((key: any) => key?.count) || []
      const daysDiff = moment(whereWhen?.end).diff(moment(whereWhen?.start), 'days')

      const XData =
        daysDiff > 30 && daysDiff < 365
          ? AllGraphData?.graphData?.map((key: any) => moment(key?._id).format('MMM'))
          : daysDiff > 365
          ? AllGraphData?.graphData?.map((key: any, index: number) =>
              index === 0 || index === AllGraphData?.graphData?.length - 1 ? key?._id : ''
            )
          : AllGraphData?.graphData?.map((key: any, index: number) =>
              index === 0 || index === AllGraphData?.graphData?.length - 1
                ? moment(key?._id).format('DD MMM')
                : ''
            ) || []

      return {
        series: [{name: 'Count', data}],
        options: {
          chart: {
            type: 'bar',
            height: 180,
            zoom: {enabled: false},
            toolbar: {show: false},
            animations: {enabled: true, speed: 400},
          },
          colors: ['#009ef7'],
          plotOptions: {bar: {horizontal: false, columnWidth: '55%', borderRadius: 4}},
          dataLabels: {enabled: false},
          stroke: {show: true, width: 2, colors: ['transparent']},
          xaxis: {categories: XData, labels: {show: true}},
          fill: {opacity: 1},
          tooltip: {
            custom: ({series, seriesIndex, dataPointIndex}: any) => {
              const dateLabel = AllGraphData?.graphData?.map((key: any) => key?._id)
              const category = dateLabel[dataPointIndex]
              const value = series[seriesIndex][dataPointIndex]
              return `<div class="custom-tooltip p-3"><span class="d-block fw-semibold">${category}</span><span class="fs-4 fw-bold text-primary">${value}</span></div>`
            },
          },
        },
      }
    },
    [whereWhen]
  )

  const smsLineData = useMemo(
    () =>
      LineGraphData?.gatewaySMSData?.[0]?.graphData?.map((key: any) => [
        +moment.utc(key?._id).format('x'),
        key?.count,
      ]) || [],
    [LineGraphData?.gatewaySMSData]
  )

  const mmsLineData = useMemo(
    () =>
      LineGraphData?.gatewayMMSData?.[0]?.graphData?.map((key: any) => [
        +moment.utc(key?._id).format('x'),
        key?.count,
      ]) || [],
    [LineGraphData?.gatewayMMSData]
  )

  const cogLineData = useMemo(
    () =>
      LineGraphData?.totalCOGData?.graphData?.map((key: any) => [
        +moment.utc(key?._id).format('x'),
        key?.count,
      ]) || [],
    [LineGraphData?.totalCOGData]
  )

  // Message Sent Metrics Data
  const messageSentData = useMemo(
    () => ({
      totalSms: AdminDashboardCount?.totalSentSMS?.[0]?.count || 0,
      totalSmsParts: AdminDashboardCount?.totalSmsParts?.[0]?.count || 0,
      totalMms: AdminDashboardCount?.totalSentMMS?.[0]?.count || 0,
      totalMmsParts: AdminDashboardCount?.totalMmsParts?.[0]?.count || 0,
      providers: [
        {
          provider: 'Sinch',
          sent: AdminDashboardCount?.sinchSent?.[0]?.count || 0,
          parts: AdminDashboardCount?.sinchParts?.[0]?.count || 0,
          loading: AdminDashboardCountLoading,
        },
        {
          provider: 'Modica',
          sent: AdminDashboardCount?.modicaSent?.[0]?.count || 0,
          parts: AdminDashboardCount?.modicaParts?.[0]?.count || 0,
          loading: AdminDashboardCountLoading,
        },
        {
          provider: 'Telstra',
          sent: AdminDashboardCount?.telstraSent?.[0]?.count || 0,
          parts: AdminDashboardCount?.telstraParts?.[0]?.count || 0,
          loading: AdminDashboardCountLoading,
        },
      ],
      loading: AdminDashboardCountLoading,
    }),
    [AdminDashboardCount, AdminDashboardCountLoading]
  )

  // COG Metrics Data
  const cogData = useMemo(() => {
    // Fallback COG rates per message part
    const SINCH_COG_RATE = 0.023
    const MODICA_COG_RATE = 0.016
    const TELSTRA_COG_RATE = 0.018 // Telstra rate similar to other providers

    // Get parts count for fallback calculation
    const sinchParts = AdminDashboardCount?.sinchParts?.[0]?.count || 0
    const modicaParts = AdminDashboardCount?.modicaParts?.[0]?.count || 0
    const telstraParts = AdminDashboardCount?.telstraParts?.[0]?.count || 0

    // Calculate COG with fallback using parts from the same endpoint
    const sinchCOG = AdminDashboardCount?.sinchCOG?.[0]?.total || sinchParts * SINCH_COG_RATE || 0
    const modicaCOG =
      AdminDashboardCount?.modicaCOG?.[0]?.total || modicaParts * MODICA_COG_RATE || 0
    const telstraCOG =
      AdminDashboardCount?.telstraCOG?.[0]?.total || telstraParts * TELSTRA_COG_RATE || 0

    return {
      providers: [
        {
          provider: 'Sinch',
          cost: sinchCOG,
          loading: AdminDashboardCountLoading,
        },
        {
          provider: 'Modica',
          cost: modicaCOG,
          loading: AdminDashboardCountLoading,
        },
        {
          provider: 'Telstra',
          cost: telstraCOG,
          loading: AdminDashboardCountLoading,
        },
      ],
      totalCOG: sinchCOG + modicaCOG + telstraCOG,
      loading: AdminDashboardCountLoading,
    }
  }, [AdminDashboardCount, AdminDashboardCountLoading])

  // Sales & Contacts Metrics Data (from separate endpoint)
  const salesContactsMetrics = useMemo(
    () => ({
      totalSales: AdminDashboardSalesContacts?.totalSales?.[0]?.totoalAmount || 0,
      totalContacts: AdminDashboardSalesContacts?.totalContacts?.[0]?.count || 0,
      newContacts: AdminDashboardCount?.newUsers?.[0]?.count || 0,
      loading: AdminDashboardSalesContactsLoading || AdminDashboardCountLoading,
    }),
    [
      AdminDashboardSalesContacts,
      AdminDashboardCount,
      AdminDashboardSalesContactsLoading,
      AdminDashboardCountLoading,
    ]
  )

  // Render tab content
  const renderTabContent = useCallback(() => {
    switch (activeTab) {
      case 'analytics':
        return (
          <div className='charts-grid animate-fade-in'>
            <ChartCard
              title='SMS Messages Sent'
              dateRange={dateRangeText}
              totalValue={GraphData?.gatewaySMSData?.[0]?.totalCount?.[0]?.total?.toFixed(0)}
              graphData={GraphData?.gatewaySMSData?.[0]}
              createChartOptions={createBarChartOptions}
              hasData={(GraphData?.gatewaySMSData?.[0]?.graphData?.length ?? 0) > 0}
              loading={GraphDataLoading}
            />
            <ChartCard
              title='MMS Messages Sent'
              dateRange={dateRangeText}
              totalValue={GraphData?.gatewayMMSData?.[0]?.totalCount?.[0]?.total}
              graphData={GraphData?.gatewayMMSData?.[0]}
              createChartOptions={createBarChartOptions}
              hasData={(GraphData?.gatewayMMSData?.[0]?.graphData?.length ?? 0) > 0}
              loading={GraphDataLoading}
            />
            {isAdmin(Admin?.role) && (
              <ChartCard
                title='New Users'
                dateRange={dateRangeText}
                totalValue={GraphData?.newContactData?.[0]?.totalCount?.[0]?.total}
                graphData={GraphData?.newContactData?.[0]}
                createChartOptions={createBarChartOptions}
                hasData={(GraphData?.newContactData?.[0]?.graphData?.length ?? 0) > 0}
                loading={GraphDataLoading}
              />
            )}
          </div>
        )

      case 'notifications':
        return (
          <Suspense fallback={<LoadingFallback height={300} text='Loading notifications...' />}>
            <NotificationTable />
          </Suspense>
        )

      case 'users':
        return (
          <Suspense fallback={<LoadingFallback height={300} text='Loading users...' />}>
            <NewUserTable />
          </Suspense>
        )

      case 'trends':
        return (
          <MessageTrendsChart
            smsData={smsLineData}
            mmsData={mmsLineData}
            cogData={cogLineData}
            smsTotalCount={LineGraphData?.gatewaySMSData?.[0]?.totalCount?.[0]?.total || 0}
            mmsTotalCount={LineGraphData?.gatewayMMSData?.[0]?.totalCount?.[0]?.total || 0}
            cogTotalCount={LineGraphData?.totalCOGData?.totalCount?.[0]?.total || 0}
            type={type}
            onTypeChange={setType}
            isUserWhiteLabel={isUserWhiteLabel}
          />
        )

      case 'table':
        return (
          <Suspense fallback={<LoadingFallback height={300} text='Loading table...' />}>
            <DashboardTablePage accountIdData={accountIdData} />
          </Suspense>
        )

      case 'tickets':
        return (
          <Suspense fallback={<LoadingFallback height={300} text='Loading tickets...' />}>
            <SupportTicketModule whereWhen={whereWhen} />
          </Suspense>
        )

      case 'config':
        return (
          <Suspense fallback={<LoadingFallback height={300} text='Loading configuration...' />}>
            <ConfigDashboard
              whereWhen={whereWhen}
              accountIdData={accountIdData}
              isUserWhiteLabel={isUserWhiteLabel}
            />
          </Suspense>
        )

      default:
        return null
    }
  }, [
    activeTab,
    dateRangeText,
    GraphData,
    GraphDataLoading,
    createBarChartOptions,
    Admin?.role,
    smsLineData,
    mmsLineData,
    cogLineData,
    LineGraphData,
    type,
    isUserWhiteLabel,
    accountIdData,
    whereWhen,
  ])

  return (
    <div className='enhanced-dashboard'>
      {/* Email Verification Alert */}
      {UserDetails?.data?.isEmailVerified === false && (
        <div className='d-flex justify-content-center mb-5'>
          <Alert variant='warning' className='w-500px alert-message'>
            <KTSVG
              path='/media/icons/duotune/general/gen044.svg'
              className='svg-icon-2x svg-icon-warning me-4'
            />
            Your email hasn't been verified, please{' '}
            <Alert.Link
              href='#'
              className='text-primary'
              onClick={() => {
                setVisible(true)
                dispatch(sendEmailOtp({email}))
              }}
            >
              verify your email
            </Alert.Link>
          </Alert>
        </div>
      )}

      {/* Filters Section */}
      <div className='filters-section d-flex flex-wrap justify-content-end align-items-center gap-3 mb-6'>
        {(!isStaff(Admin?.role, true) || isAdmin(Admin?.role)) && !isUserWhiteLabel && (
          <Select
            className='react-select-container account-select'
            classNamePrefix='react-select'
            placeholder='All Accounts'
            onChange={(e: any) => setAccountIdData(e.value)}
            options={[
              {value: '', label: 'All Accounts'},
              ...(accountIdList?.map((key: any) => ({
                value: key?._id,
                label: key?.companyName,
              })) || []),
            ]}
          />
        )}

        <div className='date-range-picker d-flex align-items-center gap-2'>
          <div className='date-input-wrapper'>
            <DatePicker
              dateFormat='dd MMM yyyy'
              className='form-control form-control-sm'
              selected={whereWhen?.start}
              onChange={(date: any) => setWhereWhen({...whereWhen, start: date})}
              placeholderText='Start date'
            />
            <i className='fas fa-calendar-alt date-icon'></i>
          </div>
          <span className='text-muted fs-7 fw-medium'>to</span>
          <div className='date-input-wrapper'>
            <DatePicker
              dateFormat='dd MMM yyyy'
              className='form-control form-control-sm'
              selected={whereWhen?.end}
              onChange={(date: any) => setWhereWhen({...whereWhen, end: date})}
              minDate={whereWhen?.start}
              placeholderText='End date'
            />
            <i className='fas fa-calendar-alt date-icon'></i>
          </div>
        </div>
      </div>

      {/* Key Metrics Section - Always visible */}
      <div className='mb-8'>
        <h2 className='fs-3 fw-bold text-dark mb-4'>Key Metrics</h2>
        <div className='metrics-group-grid'>
          {/* Message Sent Card */}
          <MessageSentCard
            totalSms={messageSentData.totalSms}
            totalSmsParts={messageSentData.totalSmsParts}
            totalMms={messageSentData.totalMms}
            totalMmsParts={messageSentData.totalMmsParts}
            providers={messageSentData.providers}
            loading={messageSentData.loading}
          />

          {/* COG Card */}
          <COGCard
            providers={cogData.providers}
            totalCOG={cogData.totalCOG}
            loading={cogData.loading}
          />

          {/* Sales & Contacts Card */}
          <SalesContactsCard
            totalSales={salesContactsMetrics.totalSales}
            totalContacts={salesContactsMetrics.totalContacts}
            newContacts={salesContactsMetrics.newContacts}
            loading={salesContactsMetrics.loading}
          />
        </div>
      </div>

      {/* Horizontal Tabs Navigation */}
      <div className='dashboard-tabs-container'>
        <Tab.Container activeKey={activeTab} onSelect={(k) => k && setActiveTab(k)}>
          {/* Tab Navigation */}
          <Nav variant='tabs' className='dashboard-nav-tabs mb-6'>
            {tabConfig.map((tab) => (
              <Nav.Item key={tab.id}>
                <Nav.Link eventKey={tab.id} className='d-flex align-items-center gap-2 px-4 py-3'>
                  <KTSVG path={tab.icon} className='svg-icon-2' />
                  <span className='fw-semibold'>{tab.label}</span>
                </Nav.Link>
              </Nav.Item>
            ))}
          </Nav>

          {/* Tab Content */}
          <Tab.Content className='dashboard-tab-content'>
            <Tab.Pane eventKey={activeTab} className='animate-fade-in'>
              {renderTabContent()}
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>
      </div>
    </div>
  )
}

export default React.memo(DashboardPageEnhanced)
